import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { C, PageHeader, GlassCard, Badge, Button, Skeleton } from "@coremonit/design-system";
import { Map, ZoomIn, ZoomOut, Maximize2, ArrowLeft } from "lucide-react";
import { useClusterTopology } from "../api/hooks";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { statusColor } from "@coremonit/design-system";

export function ClusterTopologyPage() {
  const { id } = useParams();
  const topo = useClusterTopology(id);
  const nav = useNavigate();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const bounds = useMemo(() => {
    const nodes = topo.data?.nodes ?? [];
    if (!nodes.length) return { w: 800, h: 600 };
    const maxX = Math.max(...nodes.map((n) => n.x)) + 120;
    const maxY = Math.max(...nodes.map((n) => n.y)) + 80;
    return { w: maxX, h: maxY };
  }, [topo.data]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "calc(100vh - 120px)" }}>
      <Breadcrumbs items={[
        { label: "Organization", path: "/" },
        { label: id ?? "Cluster", path: `/clusters/${id}` },
        { label: "Topology" },
      ]} />
      <PageHeader
        icon={<Map size={16} />}
        title="Infrastructure map"
        subtitle={id ? `Interactive topology for ${id} — click any node to inspect` : ""}
        actions={
          <>
            <Button variant="secondary" icon={<ArrowLeft size={12} />} onClick={() => nav(`/clusters/${id}`)}>Cluster overview</Button>
            <Button variant="secondary" icon={<ZoomOut size={12} />} onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} />
            <Button variant="secondary" icon={<ZoomIn size={12} />} onClick={() => setZoom((z) => Math.min(2, z + 0.1))} />
            <Button variant="secondary" icon={<Maximize2 size={12} />} onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} />
          </>
        }
      />

      <GlassCard style={{ flex: 1, padding: 0, overflow: "hidden", position: "relative" }}>
        {topo.isLoading ? <Skeleton height="100%" /> : (
          <div style={{ width: "100%", height: "100%", overflow: "auto", background: `radial-gradient(circle at 30% 20%, ${C.blueDim} 0%, ${C.bg} 45%)` }}>
            <svg
              width={bounds.w * zoom}
              height={bounds.h * zoom}
              style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}
            >
              {(topo.data?.edges ?? []).map((e) => {
                const from = topo.data!.nodes.find((n) => n.id === e.from);
                const to = topo.data!.nodes.find((n) => n.id === e.to);
                if (!from || !to) return null;
                const color = (e.errorPct ?? 0) > 1 ? C.red : (e.latencyMs ?? 0) > 100 ? C.amber : C.borderBright;
                return (
                  <g key={e.id}>
                    <line x1={from.x * zoom} y1={from.y * zoom} x2={to.x * zoom} y2={to.y * zoom} stroke={color} strokeWidth={1.5} opacity={0.7} />
                    <text x={((from.x + to.x) / 2) * zoom} y={((from.y + to.y) / 2) * zoom - 4} fill={C.textMuted} fontSize={8} textAnchor="middle">
                      {e.latencyMs ? `${Math.round(e.latencyMs)}ms` : ""}{e.errorPct ? ` · ${e.errorPct.toFixed(1)}% err` : ""}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              {(topo.data?.nodes ?? []).map((n) => (
                <button
                  key={n.id}
                  onClick={() => nav(n.path)}
                  style={{
                    position: "absolute",
                    left: n.x * zoom + pan.x - 50,
                    top: n.y * zoom + pan.y - 22,
                    width: 100, pointerEvents: "auto",
                    padding: "8px 6px", borderRadius: 10,
                    background: C.surface, border: `1px solid ${statusColor(n.status)}55`,
                    boxShadow: `0 4px 20px rgba(0,0,0,0.35)`,
                    cursor: "pointer", textAlign: "center",
                  }}
                >
                  <div style={{ color: C.textMuted, fontSize: 8, textTransform: "uppercase" }}>{n.kind}</div>
                  <div style={{ color: C.text, fontSize: 10, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.name}</div>
                  {n.metrics && (
                    <div style={{ color: C.textMuted, fontSize: 8, marginTop: 2 }}>
                      {n.metrics.rps ?? 0} rps · {n.metrics.latencyMs ?? 0}ms
                    </div>
                  )}
                  <Badge color={n.status === "healthy" ? "green" : n.status === "warning" ? "amber" : "red"}>{n.status}</Badge>
                </button>
              ))}
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
